import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AlertCircle, CheckCircle, XCircle, Clock, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'

interface BulkOperationProgressProps {
  operationId: string | null
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

interface OperationStatus {
  id: string
  operation_type: string
  total_selected: number
  successful_count: number
  failed_count: number
  status: 'running' | 'completed' | 'failed'
  errors: string[]
  progress_details?: {
    current_batch?: number
    total_batches?: number
    processing_stage?: string
    estimated_time_remaining?: string
    current_item?: string
    processing_speed?: string
  }
  started_at: string
  completed_at?: string
}

export function BulkOperationProgress({ 
  operationId, 
  isOpen, 
  onClose, 
  onComplete 
}: BulkOperationProgressProps) {
  const [operation, setOperation] = useState<OperationStatus | null>(null)
  const [progress, setProgress] = useState(0)
  const [isPolling, setIsPolling] = useState(false)
  const [hasForceCompleted, setHasForceCompleted] = useState(false)

  useEffect(() => {
    if (!operationId || !isOpen) return

    const pollOperation = async () => {
      try {
        const { data, error } = await supabase
          .from('mailbox_bulk_operations')
          .select('*')
          .eq('id', operationId)
          .single()

        if (error) throw error

        const operationData: OperationStatus = {
          ...data,
          errors: Array.isArray(data.errors) ? 
            data.errors.map(e => typeof e === 'string' ? e : JSON.stringify(e)) : 
            (data.errors ? [String(data.errors)] : []),
          status: data.completed_at ? 
            (data.failed_count > 0 && data.successful_count === 0 ? 'failed' : 'completed') :
            'running',
          progress_details: data.progress_details && typeof data.progress_details === 'object' && !Array.isArray(data.progress_details) 
            ? data.progress_details as any 
            : undefined
        }

        setOperation(operationData)

        // Calculate progress - cap at 100% and fix overflow
        const total = operationData.total_selected
        const processed = Math.min(operationData.successful_count + operationData.failed_count, total)
        const progressPercent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0
        setProgress(progressPercent)

        // Check for timeout or forced completion
        const isTimedOut = !operationData.completed_at && 
          operationData.started_at && 
          (Date.now() - new Date(operationData.started_at).getTime() > 30 * 60 * 1000) // 30 minutes

        // If operation is complete, timed out, or force completed
        if (operationData.status === 'completed' || operationData.status === 'failed' || isTimedOut || hasForceCompleted) {
          setIsPolling(false)
          // Don't auto-close the dialog - let user manually close it to see results
          // if (operationData.status === 'completed' || hasForceCompleted) {
          //   onComplete()
          // }
        }
      } catch (error) {
        console.error('Error polling operation:', error)
        toast.error('Failed to get operation status')
        setIsPolling(false)
      }
    }

    // Start polling
    setIsPolling(true)
    pollOperation()
    
    const interval = setInterval(pollOperation, 2000) // Poll every 2 seconds
    
    return () => {
      clearInterval(interval)
      setIsPolling(false)
    }
  }, [operationId, isOpen, onComplete])

  const getOperationIcon = () => {
    if (!operation) return <Clock className="w-5 h-5 text-blue-500" />
    
    switch (operation.status) {
      case 'running':
        return <Clock className="w-5 h-5 text-blue-500 animate-spin" />
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <Clock className="w-5 h-5 text-gray-500" />
    }
  }

  const getOperationTitle = () => {
    if (!operation) return 'Loading Operation...'
    
    const typeMap: { [key: string]: string } = {
      'bulk_deactivate': 'Deactivating Mailboxes',
      'warmup_enable_toggle': 'Enabling Warmup',
      'warmup_disable_toggle': 'Disabling Warmup',
      'client_assignment': 'Assigning Client',
      'provider_update': 'Updating Provider',
      'bulk_delete': 'Deleting Mailboxes',
      'reply_categorization': 'Categorizing Replies'
    }
    
    return typeMap[operation.operation_type] || 'Processing Operation'
  }

  const getStatusBadge = () => {
    if (!operation) return <Badge variant="secondary">Loading...</Badge>
    
    switch (operation.status) {
      case 'running':
        return <Badge variant="secondary">In Progress</Badge>
      case 'completed':
        return <Badge variant="default" className="bg-green-500">Completed</Badge>
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge variant="secondary">Unknown</Badge>
    }
  }

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime)
    const end = endTime ? new Date(endTime) : new Date()
    const diffMs = end.getTime() - start.getTime()
    const diffSecs = Math.floor(diffMs / 1000)
    
    if (diffSecs < 60) return `${diffSecs}s`
    const diffMins = Math.floor(diffSecs / 60)
    const remainingSecs = diffSecs % 60
    return `${diffMins}m ${remainingSecs}s`
  }

  const handleForceComplete = async () => {
    if (!operation?.id) return
    
    try {
      await supabase
        .from('mailbox_bulk_operations')
        .update({
          completed_at: new Date().toISOString(),
          operation_status: 'completed'
        })
        .eq('id', operation.id)
      
      setHasForceCompleted(true)
      toast.success('Operation marked as completed')
    } catch (error) {
      toast.error('Failed to complete operation')
    }
  }

  if (!isOpen) return null

  const getDeactivationDetails = () => {
    if (operation?.operation_type === 'bulk_deactivate') {
      return (
        <div className="space-y-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <h4 className="font-medium text-orange-800">Deactivation Process</h4>
          <div className="text-sm text-orange-700 space-y-1">
            <div>• Marking mailboxes as DEACTIVATED in database</div>
            <div>• Removing mailboxes from Smartlead campaigns</div>
            <div>• Stopping all email sending for these mailboxes</div>
            <div>• Updating warmup status to INACTIVE</div>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              {getOperationIcon()}
              {getOperationTitle()}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status and Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              {getStatusBadge()}
              <span className="text-sm text-muted-foreground">
                {progress}% complete
              </span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>

          {/* Operation Details */}
          {operation && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Items:</span>
                  <span className="ml-2 font-medium">{operation.total_selected}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="ml-2 font-medium">
                    {formatDuration(operation.started_at, operation.completed_at)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Successful:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {operation.successful_count}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Failed:</span>
                  <span className="ml-2 font-medium text-red-600">
                    {operation.failed_count}
                  </span>
                </div>
              </div>

              {/* Deactivation-specific details */}
              {getDeactivationDetails()}

              {/* Progress Details */}
              {operation.progress_details && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm space-y-1">
                    {operation.progress_details.processing_stage && (
                      <div>
                        <span className="text-muted-foreground">Stage:</span>
                        <span className="ml-2">{operation.progress_details.processing_stage}</span>
                      </div>
                    )}
                    {operation.progress_details.current_item && (
                      <div>
                        <span className="text-muted-foreground">Current Item:</span>
                        <span className="ml-2 font-mono text-xs">{operation.progress_details.current_item}</span>
                      </div>
                    )}
                    {operation.progress_details.current_batch && operation.progress_details.total_batches && (
                      <div>
                        <span className="text-muted-foreground">Batch:</span>
                        <span className="ml-2">
                          {operation.progress_details.current_batch} of {operation.progress_details.total_batches}
                        </span>
                      </div>
                    )}
                    {operation.progress_details.processing_speed && (
                      <div>
                        <span className="text-muted-foreground">Speed:</span>
                        <span className="ml-2">{operation.progress_details.processing_speed}</span>
                      </div>
                    )}
                    {operation.progress_details.estimated_time_remaining && (
                      <div>
                        <span className="text-muted-foreground">ETA:</span>
                        <span className="ml-2">{operation.progress_details.estimated_time_remaining}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Errors */}
              {operation.errors && operation.errors.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {operation.errors.length} Error{operation.errors.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {operation.errors.slice(0, 5).map((error, index) => (
                      <div key={index} className="text-xs text-red-600 bg-red-50 p-2 rounded">
                        {error}
                      </div>
                    ))}
                    {operation.errors.length > 5 && (
                      <div className="text-xs text-muted-foreground text-center">
                        ... and {operation.errors.length - 5} more errors
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            {operation?.status === 'running' && !hasForceCompleted && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleForceComplete}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  Force Complete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                >
                  Run in Background
                </Button>
              </>
            )}
            {(operation?.status === 'completed' || operation?.status === 'failed' || hasForceCompleted) && (
              <Button
                variant="default"
                size="sm"
                onClick={onClose}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}